import React, { useRef } from 'react';
import * as R from 'ramda';
import styled, { css } from 'styled-components';
import { position, layout, space, shadow } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import Downshift from 'downshift';
import posed, { PoseGroup } from 'react-pose';
import Popper from '../popper/Popper';
import { Flex, Box } from '../grid/Grid';

const Content = styled.div`
  position: relative;
`;

const Animation = posed.div({
  enter: {
    opacity: 1,
    scale: 1,
    x: ({ x }) => x,
    y: ({ y }) => y,
    transition: { duration: 150, ease: 'circOut' },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    x: ({ x }) => x,
    y: ({ y }) => y,
    transition: { duration: 0, ease: 'backOut' },
    delay: 0,
  },
});

const MenuWrapper = styled(Animation)`
  display: flex;
  flex-flow: column;
  max-height: ${themeGet('dropdown.menu.maxHeight')};
  box-sizing: border-box;
  outline: 0;
  width:  160px;
  padding: 10px 0;
  border: ${themeGet('dropdown.menu.border')};
  border-color: ${themeGet('dropdown.menu.borderColor')};
  border-radius: ${themeGet('dropdown.menu.borderRadius')};
  box-shadow: ${themeGet('dropdown.menu.boxShadow')};
  background-color: ${themeGet('dropdown.menu.backgroundColor')};
  z-index: ${themeGet('dropdown.menu.zIndex')};
  transform: ${({ x }) => (x ? `translateX(${x})` : 'unset')};
  transform-origin: top center;
  ${position}
  ${layout}
  ${space}
  ${shadow}
`;

MenuWrapper.defaultProps = {
  x: 0,
};

const Menu = styled.ul`
  padding: 0;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  padding: ${themeGet('dropdown.option.padding')};
  box-sizing: border-box;
  font-size: ${themeGet('dropdown.option.fontSize')};
  border-top: ${themeGet('dropdown.option.border.top')};
  border-right: ${themeGet('dropdown.option.border.right')};
  border-bottom: ${themeGet('dropdown.option.border.bottom')};
  border-left: ${themeGet('dropdown.option.border.left')};
  box-shadow: ${themeGet('dropdown.option.boxShadow')};
  color: ${themeGet('dropdown.option.color')};
  background-color: ${themeGet('dropdown.option.backgroundColor')};
  cursor: pointer;

  ${props =>
    props.hovered &&
    css`
      color: ${themeGet('dropdown.option.hover.color')};
      background-color: ${themeGet('dropdown.option.hover.backgroundColor')};
    `}

  ${props =>
    props.selected &&
    css`
      font-weight: 700;
      color: ${themeGet('dropdown.option.selected.color')};
      background-color: ${themeGet('dropdown.option.selected.backgroundColor')};
    `}
`;

const Label = styled.h3`
  font-size: ${themeGet('dropdown.option.fontSize')};
  font-weight: normal;
  margin: 0;
  user-select: none;
`;

const OptionBlock = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  height: ${themeGet('dropdown.option.height')};
  padding: 0;
`;

/**
 * @typedef DropdownProps
 * @property {any} selectedItem
 * @property {(item) => void} onChange
 * @property {any[]} options
 * @property {boolean} disablePortal
 * @property {boolean} disabledPopper
 * @property {() => React.ReactElement} render
 * @property {'bottom' | 'bottomLeft' | 'bottomRight' | 'top' | 'topLeft' | 'topRight'} placement
 */
/**
 * @param {DropdownProps} props
 */
export const Dropdown = ({
  selectedItem,
  onChange,
  options,
  render = () => {},
  renderOption,
  menuWrapperStyle = {},
  disablePortal = false,
  disabledPopper = false,
  placement = 'bottomLeft',
  ...rest
}) => {
  const ele = useRef(null);
  return (
    <Downshift itemToString={R.identity} selectedItem={selectedItem} onChange={onChange} {...rest}>
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        getRootProps,
        isOpen,
        selectedItem,
        highlightedIndex,
        toggleMenu,
      }) => {
        const defaultRenderOption = item => (
          <Item key={item.name}>
            <OptionBlock>
              <Flex flexDirection="row" alignItems="center">
                <Box mr="5px">{item.icon}</Box>
                <Label>{item.name}</Label>
              </Flex>
            </OptionBlock>
          </Item>
        );

        const renderOptionCb = renderOption || defaultRenderOption;
        const rootProps = getRootProps();
        return (
          <Content
            {...rootProps}
            ref={node => {
              ele.current = node;
              rootProps.ref(node);
            }}
          >
            {render({
              getInputProps,
              getMenuProps,
              getItemProps,
              isOpen,
              selectedItem,
              highlightedIndex,
              toggleMenu,
            })}
            <Popper
              key="popper"
              disabledPopper={disabledPopper}
              disablePortal={disablePortal}
              keepMounted
              open={isOpen && options}
              referRef={ele}
              placement={placement}
            >
              {({ open }) => (
                <PoseGroup>
                  {open && (
                    <MenuWrapper key="menu" {...menuWrapperStyle}>
                      <Menu {...getMenuProps()}>
                        {R.pipe(
                          R.map(renderOptionCb),
                          R.addIndex(R.map)((item, index) =>
                            React.cloneElement(
                              item,
                              getItemProps({
                                item: options[index],
                                index,
                                hovered: highlightedIndex === index,
                                selected: selectedItem === options[index],
                                disabled: item.disabled,
                              })
                            )
                          )
                        )(options)}
                      </Menu>
                    </MenuWrapper>
                  )}
                </PoseGroup>
              )}
            </Popper>
          </Content>
        );
      }}
    </Downshift>
  );
};
